import { useState, useEffect } from 'react';

import { apiRequest } from '@/src/services';
import { SimpleSwitch, ErrorMessage, Loading, TTSButton } from '@/src/ui';
import { Constants } from '@/src/utils';
import {
    DynamicTextPreview,
    MultipleChoiceRenderer,
    OpenTextRenderer,
    MultipleSelectRenderer,
    MatrixRenderer,
    DragAndDropRenderer,
    GraphingRenderer,
    type QtiQuestionDTO,
    type QtiMultipleChoiceQuestionDTO,
    type QtiOpenTextQuestionDTO,
    type QtiMultipleSelectQuestionDTO,
    type QtiMatrixQuestionDTO,
    type QtiDragAndDropQuestionDTO,
    type QtiGraphingQuestionDTO,
    type QtiStimulusQuestionDTO,
    type QuestionRendererOptions,
} from '@/src/lib';
const qtm = Constants.QUESTION_TYPE_MASTER_IDS;

interface StimulusRendererProps {
    question: QtiStimulusQuestionDTO;
    options: QuestionRendererOptions;
}

export default function StimulusRenderer({ question, options }: StimulusRendererProps) {
    const { showScoring = false, showAudioButtons = false } = options;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [language, setLanguage] = useState<'en' | 'es'>('en');
    const [questionData, setQuestionData] = useState<QtiQuestionDTO[]>([]);

    const optionsToPass = {
        showScoring,
        showAudioButtons,
        language,
    };

    useEffect(() => {
        (async function () {
            if (question) {
                setLoading(true);
                const questionsToFetch = question.selectedQuestions || [];
                const newQuestionData: QtiQuestionDTO[] = [];

                async function fetchQuestion(q: QtiQuestionDTO, index: number) {
                    const res = await apiRequest<QtiQuestionDTO>(
                        'get',
                        `/assessments/questions/${q.id}`
                    );
                    newQuestionData.push(res.data);
                    if (questionsToFetch[index + 1]) {
                        await fetchQuestion(questionsToFetch[index + 1], index + 1);
                    }
                }

                try {
                    await fetchQuestion(questionsToFetch[0], 0);
                } catch {
                    setError('Failed to load child question data');
                } finally {
                    setLoading(false);
                }

                setQuestionData(newQuestionData);
            }
        })();
    }, [question]);

    if (!question) return 'Unable to load question';
    if (loading) return <Loading />;
    return (
        <div>
            <ErrorMessage error={error} />
            <div className="flex flex-row gap-x-8">
                <div className="font-bold">Question: {question.title || 'Untitled Question'}</div>
                {question.meta.isSpanishAllowed && (
                    <div className="ml-auto">
                        <SimpleSwitch
                            name="toggleSpanish"
                            checked={language === 'es'}
                            onChange={(e) => setLanguage(e.target.checked ? 'es' : 'en')}
                            label="View Spanish"
                        />
                    </div>
                )}
            </div>
            <div className="mb-4 flex flex-row items-end">
                <DynamicTextPreview content={question.prompt || 'No prompt provided.'} />
                {showAudioButtons && (
                    <TTSButton
                        text={language === 'en' ? question.prompt : question.prompt_es}
                        onError={setError}
                    />
                )}
            </div>
            {questionData.map((q) => (
                <div key={`${question.id}-${q.id}`} className="mb-6">
                    {renderSingleQuestionPreview(q, optionsToPass)}
                </div>
            ))}
        </div>
    );
}

function renderSingleQuestionPreview(question: QtiQuestionDTO, options: QuestionRendererOptions) {
    switch (question.meta.questionTypeMasterId) {
        case qtm.MULTIPLE_CHOICE:
            return (
                <MultipleChoiceRenderer
                    question={question as QtiMultipleChoiceQuestionDTO}
                    options={options}
                />
            );
        case qtm.OPEN_TEXT:
            return (
                <OpenTextRenderer question={question as QtiOpenTextQuestionDTO} options={options} />
            );
        case qtm.MULTIPLE_SELECT:
            return (
                <MultipleSelectRenderer
                    question={question as QtiMultipleSelectQuestionDTO}
                    options={options}
                />
            );
        case qtm.MATRIX:
            return <MatrixRenderer question={question as QtiMatrixQuestionDTO} options={options} />;
        case qtm.DRAG_AND_DROP:
            return (
                <DragAndDropRenderer
                    question={question as QtiDragAndDropQuestionDTO}
                    options={options}
                />
            );
        case qtm.GRAPHING:
            return (
                <GraphingRenderer question={question as QtiGraphingQuestionDTO} options={options} />
            );
        default:
            return (
                <ErrorMessage
                    error={`Unsupported question type ${question.meta.questionTypeMasterId}`}
                />
            );
    }
}
